import { Field, Label } from '@headlessui/react';
import React, { FC } from 'react';

import MinusIcon from '../../../assets/icons/minus-icon';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { Checkbox } from '../../../components/checkbox/index';
import { DetailsTab, SafeOrder, SafeSortBy, SafeWithPermissions } from '../../../types/safes.types';
import { SafeTableItem } from './safe-table-item';
import { TableSortingCell } from './table-sorting-cell';

export type DesktopDetailsProps = {
  handleConvert: (id: string) => void;
  handleDelete: (id: string) => void;
  handleEdit: (id: string) => void;
  handleEditConversion: (id: string) => void;
  handleView: (id: string) => void;
  handleViewConversion: (id: string) => void;
  isSelected: (id: string) => boolean;
  isSelectStarted: boolean;
  onRemoveSelect: (id: string) => void;
  onRemoveSelectAll: () => void;
  onSelect: (id: string) => void;
  onSelectAll: (id: string[]) => void;
  onSortClick: (field: SafeSortBy) => void;
  safes: SafeWithPermissions[];
  selectedTab: DetailsTab;
  sortField: null | SafeSortBy;
  sortMode: null | SafeOrder;
};

export const DesktopDetails: FC<DesktopDetailsProps> = ({
  safes,
  isSelectStarted,
  isSelected,
  onSelectAll,
  onRemoveSelectAll,
  onSortClick,
  onSelect,
  onRemoveSelect,
  handleView,
  handleEdit,
  handleDelete,
  handleConvert,
  sortMode,
  sortField,
  selectedTab,
  handleViewConversion,
  handleEditConversion,
}) => {
  return (
    <table className="h-fit w-full divide-y-[1px] divide-[#F2F2F2] border-b-[1px] border-[#F2F2F2] max-lg:hidden">
      <colgroup>
        <col className="w-12 shrink-0" span={1} />
      </colgroup>
      <thead>
        <tr className="h-11 bg-gray-50">
          {selectedTab === DetailsTab.PENDING_CONVERSION && (
            <td className="relative">
              <div className="flex h-full w-full items-center justify-center px-4">
                <Field className="flex cursor-pointer items-center">
                  <Checkbox
                    checked={isSelectStarted}
                    id="select-all"
                    onChange={(checked) =>
                      checked ? onSelectAll(safes?.map(({ id }) => id) || []) : onRemoveSelectAll()
                    }
                    selectIcon={<MinusIcon />}
                  />
                  <Label
                    className="absolute left-0 top-0 h-full w-full cursor-pointer"
                    htmlFor="select-all"
                  />
                </Field>
              </div>
            </td>
          )}

          <td className="pl-4">
            <TableSortingCell
              onSortClick={onSortClick}
              sortByCell={SafeSortBy.FULL_NAME}
              sortField={sortField}
              sortMode={sortMode}
              title={<AppFormattedMessage id="NAME" />}
            />
          </td>

          <td className="pl-4 text-label-md font-[450] text-[#172335]">
            <AppFormattedMessage id="INVESTMENT" />
          </td>

          {selectedTab === DetailsTab.PENDING_CONVERSION && (
            <td className="pl-4">
              <TableSortingCell
                onSortClick={onSortClick}
                sortByCell={SafeSortBy.ISSUE_DATE}
                sortField={sortField}
                sortMode={sortMode}
                title={<AppFormattedMessage id="ISSUE_DATE" />}
              />
            </td>
          )}

          {selectedTab === DetailsTab.PENDING_CONVERSION ? (
            <>
              <td className="pl-4">
                <TableSortingCell
                  onSortClick={onSortClick}
                  sortByCell={SafeSortBy.CAP_VALUE}
                  sortField={sortField}
                  sortMode={sortMode}
                  title={<AppFormattedMessage id="VAL_CAP" />}
                />
              </td>

              <td className="pl-4">
                <TableSortingCell
                  onSortClick={onSortClick}
                  sortByCell={SafeSortBy.CAP_TYPE}
                  sortField={sortField}
                  sortMode={sortMode}
                  title={<AppFormattedMessage id="CAP_TYPE" />}
                />
              </td>

              <td className="pl-4">
                <TableSortingCell
                  onSortClick={onSortClick}
                  sortByCell={SafeSortBy.DISCOUNT}
                  sortField={sortField}
                  sortMode={sortMode}
                  title={<AppFormattedMessage id="SHARE_PRICE_DISCOUNT_PERCENTAGE" />}
                />
              </td>

              <td className="pl-4">
                <TableSortingCell
                  onSortClick={onSortClick}
                  sortByCell={SafeSortBy.FLOOR}
                  sortField={sortField}
                  sortMode={sortMode}
                  title={<AppFormattedMessage id="FLOOR" />}
                />
              </td>

              <td className="pl-4">
                <TableSortingCell
                  onSortClick={onSortClick}
                  sortByCell={SafeSortBy.MATURITY_DATE}
                  sortField={sortField}
                  sortMode={sortMode}
                  title={<AppFormattedMessage id="MATURITY_DATE" />}
                />
              </td>
            </>
          ) : (
            <>
              <td className="pl-4">
                <TableSortingCell
                  onSortClick={onSortClick}
                  sortByCell={SafeSortBy.CONVERSION_DATE}
                  sortField={sortField}
                  sortMode={sortMode}
                  title={<AppFormattedMessage id="CONVERSION_DATE" />}
                />
              </td>

              <td className="pl-4">
                <TableSortingCell
                  onSortClick={onSortClick}
                  sortByCell={SafeSortBy.CONVERSION_METHOD}
                  sortField={sortField}
                  sortMode={sortMode}
                  title={<AppFormattedMessage id="CONVERSION_METHOD" />}
                />
              </td>

              <td className="pl-4 text-label-md font-[450] text-[#172335]">
                <AppFormattedMessage id="LINKED_EVENT" />
              </td>

              <td className="pl-4 text-label-md font-[450] text-[#172335]">
                <AppFormattedMessage id="SHARE_PRICE" />
              </td>

              <td className="pl-4">
                <TableSortingCell
                  onSortClick={onSortClick}
                  sortByCell={SafeSortBy.CONVERTED_SHARES}
                  sortField={sortField}
                  sortMode={sortMode}
                  title={<AppFormattedMessage id="NO_OF_SHARES" />}
                />
              </td>
            </>
          )}

          <td></td>
        </tr>
      </thead>
      <tbody className="divide-y-[1px] divide-[#F2F2F2] bg-gray-600">
        {safes?.map((safe) => (
          <SafeTableItem
            handleConvert={handleConvert}
            handleDelete={handleDelete}
            handleEdit={handleEdit}
            handleEditConversion={handleEditConversion}
            handleView={handleView}
            handleViewConversion={handleViewConversion}
            isSelected={isSelected}
            key={safe.id}
            onRemoveSelect={onRemoveSelect}
            onSelect={onSelect}
            safe={safe}
            selectedTab={selectedTab}
          />
        ))}
      </tbody>
    </table>
  );
};
